import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { fetchQuery } from 'react-relay';
import { DashboardUserBackEndQuery } from './graphQLOperations/__generated__/DashboardUserBackEndQuery.graphql';
import { getUserGraphQLBEStudent, hasStudentsRosteredQuery } from './graphQLOperations/Dashboard';
import { DashboardUserStudentBackEndQuery } from './graphQLOperations/__generated__/DashboardUserStudentBackEndQuery.graphql';
import { DashboardStudentsRosterQuery } from './graphQLOperations/__generated__/DashboardStudentsRosterQuery.graphql';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { Role } from './models/Role';
import { splitId } from './businessLogicService/splitIdsFromBackEnd';
import { MainFallback } from './components/Fallback/MainFallBack';
import { selectedAccessToken, setAcessToken } from './redux/reducers/accessTokenSlice';
import { Site } from './models/Site';
import NoUserInDB, { ErrorType } from './components/NoUserInDB/NoUserInDB';
import { Network } from './models/Network';
import { relayEnvironment } from './relay';
import { CustomBranding } from './models/User';
import { resetUser, selectUser, setUser } from './redux/reducers/userSlice';
import { usePostHog } from '@posthog/react';
import { ErrorBoundaryUI } from './components/ErrorBoundary/ErrorBoundaryUI';
import { isGraphQLAuthorizationFailure } from './utils/isGraphQLAuthorizationFailure';

type UserData = DashboardUserBackEndQuery['response'] | DashboardUserStudentBackEndQuery['response'];
const isFullUserData = (data: UserData): data is DashboardUserBackEndQuery['response'] =>
  'hasUserClassWithActiveStudent' in data;

export const Auth0ToRedux: FC<PropsWithChildren> = ({ children }) => {
  const { user, isLoading: loadingAuth0, isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const accessTokenRedux = useAppSelector(selectedAccessToken);
  const posthog = usePostHog();

  const [errorType, setErrorType] = useState<ErrorType | null>(null);
  const [hasGraphQLError, setHasGraphQLError] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const auth0InactiveUserUrl = window.location.search.includes('blocked');

  const showErrorScreen = (): void => {
    // Teacher and student variants render identical screens, so no role lookup is needed.
    // A blocked URL selects deactivation even if the user is authenticated.
    setErrorType(auth0InactiveUserUrl ? 'teacherDeactivated' : 'teacherSsoMismatch');
  };

  useEffect(() => {
    const getToken = async () => {
      if (isAuthenticated) {
        try {
          const accessToken = await getAccessTokenSilently();
          dispatch(setAcessToken(accessToken));
        } catch (_e) {
          dispatch(resetUser());
          posthog.reset();
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
    };

    getToken();
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated || !user?.sub || !accessTokenRedux) return;

      try {
        const studentData = await fetchQuery<DashboardUserStudentBackEndQuery>(
          relayEnvironment(getAccessTokenSilently, logout, posthog),
          getUserGraphQLBEStudent,
          { publicUserId: user.sub },
        ).toPromise();
        if (!studentData) {
          setUserData(null);
          return;
        }
        const userByPublicUserId = studentData.userByPublicUserId;
        if (!userByPublicUserId) {
          setUserData(studentData);
          return;
        }
        const roles = userByPublicUserId.roles ?? [];
        const isStudent = roles.some(r => (r?.name ?? '').toLowerCase() === 'student');
        if (isStudent) {
          setUserData(studentData);
          return;
        }
        // Non-student: reuse the user data already fetched above and only fetch the
        // teacher/admin-only `hasUserClassWithActiveStudent` flag (a single-field query),
        // instead of re-fetching the whole user payload via getUserGraphQLBE.
        let hasUserClassWithActiveStudent = true;
        try {
          const rosterData = await fetchQuery<DashboardStudentsRosterQuery>(
            relayEnvironment(getAccessTokenSilently, logout, posthog),
            hasStudentsRosteredQuery,
            {},
          ).toPromise();
          hasUserClassWithActiveStudent = rosterData?.hasUserClassWithActiveStudent ?? true;
        } catch (_e) {
          // We already have valid user data; don't block login on the roster flag.
          hasUserClassWithActiveStudent = true;
        }

        setUserData({ ...studentData, hasUserClassWithActiveStudent } as DashboardUserBackEndQuery['response']);
      } catch (error) {
        // A failed request is only an account outcome when the server explicitly denied access.
        if (!isGraphQLAuthorizationFailure(error)) {
          setHasGraphQLError(true);
          return;
        }
        showErrorScreen();
      }
    };

    fetchUserData();
  }, [isAuthenticated, user?.sub, accessTokenRedux]);

  const setDefaultActiveTimeZone = (sites: Site[]) => {
    if (sites && sites.length) {
      return sites[0].timeZone?.name;
    }
    return '';
  };

  useEffect(() => {
    const userByPublicUserId = userData?.userByPublicUserId;
    if (userData && user && !loadingAuth0 && isAuthenticated && userByPublicUserId) {
      const gradeLevels = userByPublicUserId.gradeLevels;
      const hasUserClassWithActiveStudent = isFullUserData(userData) ? userData.hasUserClassWithActiveStudent : true;
      dispatch(
        setUser({
          id: splitId(userByPublicUserId.id),
          email: user.email ?? '',
          updated_at: user.updated_at ?? '',
          email_verified: user.email_verified ?? false,
          nickname: user.nickname ?? '',
          picture: user.picture ?? '',
          name: user.name ?? '',
          firstName: userByPublicUserId.firstName ?? '',
          lastName: userByPublicUserId.lastName ?? '',
          sub: user.sub ?? '',
          roles: userByPublicUserId?.roles as Role[],
          gradeLevel: gradeLevels?.length ? gradeLevels[0] : null,
          sites: userByPublicUserId.sites as unknown as Site[],
          activeTimeZone: setDefaultActiveTimeZone(userByPublicUserId.sites as unknown as Site[]) || '',
          hasUserClassWithActiveStudent,
          network: userByPublicUserId.sites ? (userByPublicUserId.sites[0]?.network as Network) : null,
          customBrandingApplied: CustomBranding.NO_APPLY,
          userGuidingHash: userData.userGuidingHash,
          isInternalUser: userData.isInternalUser,
        }),
      );
    } else if (
      (userData && user && !loadingAuth0 && isAuthenticated && userData.userByPublicUserId === null) ||
      auth0InactiveUserUrl
    ) {
      if (!errorType && !hasGraphQLError) {
        showErrorScreen();
      }
    } else if (!loadingAuth0 && !isAuthenticated) {
      dispatch(resetUser());
    }
  }, [user, userData, isAuthenticated, loadingAuth0]);

  return (
    <>
      {hasGraphQLError ? (
        <ErrorBoundaryUI />
      ) : errorType ? (
        <NoUserInDB auth0User={user} errorType={errorType} />
      ) : loadingAuth0 || (!loadingAuth0 && isAuthenticated && !userRedux) ? (
        <MainFallback />
      ) : (
        children
      )}
    </>
  );
};
